import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api, fmtBps, pctToDec, isYmd, todayYmd } from "@/lib/api";
import { PageHeader, Btn, Modal, Input, Section, Select, ComboSelect, Badge } from "@/components/Shared";
import { Plus, Pencil, Trash2, Upload, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown, KeyRound, PowerOff, Power, X } from "lucide-react";
import GrantAccessModal from "@/components/GrantAccessModal";
import RateHistoryTab from "@/components/RateHistoryTab";
import RateChangeDialog from "@/components/RateChangeDialog";
import ModalTabs from "@/components/ModalTabs";
import MerchantBulkSettlementModal, { SETTLEMENT_OPTIONS } from "@/pages/merchants/MerchantBulkSettlementModal";
import MerchantDeactivateModal from "@/pages/merchants/MerchantDeactivateModal";
import MerchantImportReportModal from "@/pages/merchants/MerchantImportReportModal";
import MerchantsTable from "@/pages/merchants/MerchantsTable";
import MerchantNameAutocomplete from "@/pages/merchants/MerchantNameAutocomplete";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

const EMPTY = { merchant_id: "", merchant_name: "", parent_group: "", notes: "", gateway: "", lob: "", partner_id: "P000", merchant_rate: "", status: "Active", settlement_type: "", secondary_partner_id: "", secondary_share_type: "Percentage", secondary_share_value: "", co_partner_shares: [] };
// Fields the operator must fill before a merchant can be saved. Mirrors the
// finance-team checklist: identity (id+name), lifecycle (status), commercials
// (gateway, lob, rate). Partner is intentionally NOT here — many merchants
// onboarded under direct-sales have no partner attribution; the field is
// optional and stays as P000 ("no partner") in that case. settlement_type
// is also optional — existing rows pre-date the field.
const REQUIRED_FIELDS = [
  ["merchant_id", "Merchant ID"],
  ["merchant_name", "Merchant Name"],
  ["status", "Status"],
  ["gateway", "Gateway"],
  ["lob", "LOB"],
  ["merchant_rate", "Merchant Rate (%)"],
];

export default function Merchants() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [meta, setMeta] = useState({ lobs: [] });
  // MoM volume deltas — { current_period, prior_period, by_merchant: {mid: {current, prior, delta_pct}} }
  // Used by MerchantsTable to render a ▲/▼ badge next to each merchant name.
  const [volumeDeltas, setVolumeDeltas] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [grantFor, setGrantFor] = useState(null);  // merchant row → opens GrantAccessModal
  // Bulk-edit settlement_type — admin selects rows, picks IS/T+1, applies in one shot.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkSettlement, setBulkSettlement] = useState({ open: false, value: "" });
  const [q, setQ] = useState("");
  const [filterGateway, setFilterGateway] = useState("");
  const [filterLob, setFilterLob] = useState("");
  const [filterPartner, setFilterPartner] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortBy, setSortBy] = useState({ key: "merchant_id", dir: "asc" });  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);  // summary dialog after bulk import (null when closed)
  const [deactivateFor, setDeactivateFor] = useState(null);  // PR-D: row being deactivated, null when modal closed
  const [deactivateForm, setDeactivateForm] = useState({ deactivation_date: "", reason: "" });
  const [deactivating, setDeactivating] = useState(false);
  const [showRateCardForm, setShowRateCardForm] = useState(false);
  const [newRateValue, setNewRateValue] = useState("");
  const [modalTab, setModalTab] = useState("form");  // form | rate-history
  // PR-A.5 — when admin edits scalar merchant_rate on an existing merchant,
  // capture the change as a rate-version row (with effective_from). Holds
  // the pending payload while the dialog asks for the date.
  const [rateChangePending, setRateChangePending] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [r, p, g, prg, m, vd] = await Promise.all([
      api.get("/merchants"), api.get("/partners"), api.get("/gateways"), api.get("/programs"), api.get("/meta"),
      api.get("/merchants/volume-deltas").catch(() => ({ data: null })),  // best-effort — badge just doesn't render if this fails
    ]);
    setRows(r.data); setPartners(p.data); setGateways(g.data); setPrograms(prg.data); setMeta(m.data);
    setVolumeDeltas(vd.data);
  };
  useEffect(() => { load(); }, []);

  const taggedByMerchant = useMemo(() => {
    const map = {};
    for (const p of programs) {
      if (!p.referred_partner_id || p.referred_partner_id === "P000") continue;
      (map[p.merchant_id] = map[p.merchant_id] || new Set()).add(p.referred_partner_id);
    }
    return map;
  }, [programs]);
  const programByMid = useMemo(() => {
    const map = {};
    for (const p of programs) {
      // Prefer the auto-program M-{merchant_id}; else any program with matching merchant_id
      if (!map[p.merchant_id] || p.program_id === `M-${p.merchant_id}`) map[p.merchant_id] = p;
    }
    return map;
  }, [programs]);

  const partnerName = (pid) => partners.find((p) => p.partner_id === pid)?.partner_name || pid;

  const pgBaseRate = useMemo(() => {
    const g = gateways.find((x) => x.gateway_name === form.gateway);
    return g?.lob_rates?.find((r) => r.lob === form.lob)?.base_rate ?? null;
  }, [gateways, form.gateway, form.lob]);

  // LOB options restricted to those configured on the selected gateway —
  // prevents (Gateway, LOB) conflicts like "Lyra PG + Edu 90" when admin
  // hasn't actually defined a base rate for that pair. Empty until a
  // gateway is picked. Falls back to the global LOB list ONLY when no
  // gateway is selected (so admins can still type-ahead before locking
  // the gateway in).
  const gatewayLobOptions = useMemo(() => {
    if (!form.gateway) return [];
    const g = gateways.find((x) => x.gateway_name === form.gateway);
    return Array.from(new Set((g?.lob_rates || []).map((r) => r.lob).filter(Boolean))).sort();
  }, [gateways, form.gateway]);

  const partnerHasRateCard = useMemo(() => {
    if (!form.partner_id || form.partner_id === "P000") return null;
    if (!form.gateway || !form.lob) return null;
    const p = partners.find((x) => x.partner_id === form.partner_id);
    // Revenue-share partners don't use rate cards; treat as "not applicable"
    if (p?.compensation_model === "revenue_share") return "na";
    return p?.rate_cards?.find((r) => r.gateway === form.gateway && r.lob === form.lob) ?? false;
  }, [partners, form.partner_id, form.gateway, form.lob]);
  const partnerBaseRate = (partnerHasRateCard && partnerHasRateCard !== "na") ? partnerHasRateCard.partner_base_rate : null;
  const partnerCommissionBps = (Number(form.merchant_rate || 0) / 100 - (partnerBaseRate || 0)) * 10000;
  const showConflict = form.partner_id && form.partner_id !== "P000" && form.gateway && form.lob && partnerHasRateCard === false;

  const addRateCardOnFly = async () => {
    if (!newRateValue || isNaN(Number(newRateValue))) { toast.error("Enter a valid base rate"); return; }
    const partner = partners.find((x) => x.partner_id === form.partner_id);
    if (!partner) return;
    const updated = {
      ...partner,
      rate_cards: [...(partner.rate_cards || []), { gateway: form.gateway, lob: form.lob, partner_base_rate: Number(newRateValue) }],
    };
    try {
      await api.put(`/partners/${partner.partner_id}`, updated);
      toast.success(`Rate card added for ${partner.partner_name}`);
      setShowRateCardForm(false); setNewRateValue("");
      // reload partners only (lightweight)
      const { data } = await api.get("/partners"); setPartners(data);
    } catch (e) { toast.error("Failed to add rate card"); }
  };

  const save = async () => {
    // Mandatory-field gate. Trim strings so " " doesn't pass.
    const missing = REQUIRED_FIELDS.filter(([key]) => {
      const v = form[key];
      if (v == null) return true;
      if (typeof v === "string" && v.trim() === "") return true;
      return false;
    });
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((f) => f[1]).join(", ")}`);
      return;
    }
    try {
      // UI accepts % (e.g. 1.02); store as decimal (0.0102) — precision-safe.
      const rate = form.merchant_rate === "" || form.merchant_rate == null ? null : pctToDec(form.merchant_rate);
      // Secondary share: UI accepts % (e.g. 4 for 4%), store as decimal (0.04)
      const secVal = form.secondary_share_value === "" || form.secondary_share_value == null ? 0 : pctToDec(form.secondary_share_value);
      // iter39.3 (Option B) — Normalise co_partner_shares array. UI accepts
      // share_value in % (e.g. 5 for 5%) matching the legacy scalar convention;
      // convert to decimal fraction (0.05). Drop rows with no partner or
      // zero/blank value. Dual-write the FIRST co-partner into the legacy
      // secondary_* scalars so old consumers keep seeing meaningful data.
      const coShares = (form.co_partner_shares || [])
        .map((c) => ({
          partner_id: (c.partner_id || "").trim(),
          share_type: c.share_type || "Percentage",
          share_value: c.share_value === "" || c.share_value == null ? 0 : pctToDec(c.share_value),
        }))
        .filter((c) => c.partner_id && c.partner_id !== "P000" && c.share_value > 0);
      const legacySec = coShares[0] || null;
      const payload = {
        ...form,
        merchant_rate: rate,
        co_partner_shares: coShares,
        secondary_partner_id: legacySec ? legacySec.partner_id : "",
        secondary_share_type: legacySec ? legacySec.share_type : "Percentage",
        secondary_share_value: legacySec ? legacySec.share_value : secVal,
      };

      // PR-A.5 — On edit, if scalar merchant_rate changed, surface the
      // rate-change dialog before persisting so admin picks effective_from.
      // The dialog confirms; we then save the merchant AND post a
      // rate-version row.
      if (editId) {
        const oldRow = rows.find((r) => r.merchant_id === editId);
        const oldProg = programByMid[editId];
        const oldRate = oldRow?.merchant_rate ?? oldProg?.merchant_rate ?? null;
        const rateChanged = rate != null && oldRate != null && Number(rate) !== Number(oldRate);
        if (rateChanged) {
          setRateChangePending({ payload, oldRate, newRate: rate, merchantId: editId, merchantName: form.merchant_name });
          return;
        }
      }
      if (editId) await api.put(`/merchants/${editId}`, payload); else await api.post("/merchants", payload);
      toast.success("Saved"); setOpen(false); setShowRateCardForm(false); setModalTab("form"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Confirm callback from <RateChangeDialog>. Persists the merchant edit
  // AND creates a rate_versions row at the chosen effective_from. For
  // FUTURE-DATED rate changes the merchant's scalar `merchant_rate` is
  // intentionally NOT updated yet — the directory stays accurate until
  // the effective date arrives. The calc engine reads via rate_at().
  const confirmRateChange = async ({ effective_from, reason }) => {
    if (!rateChangePending) return;
    const { payload, newRate, oldRate, merchantId } = rateChangePending;
    // Defensive guard — string comparison of YYYY-MM-DD only works when
    // both sides are zero-padded ISO. Reject malformed input so a typo
    // can't silently overwrite the scalar.
    if (!isYmd(effective_from)) {
      toast.error("Invalid effective date — expected YYYY-MM-DD");
      return;
    }
    const isFuture = effective_from > todayYmd();
    try {
      const putBody = isFuture
        ? { ...payload, merchant_rate: Number(oldRate) }
        : payload;
      await api.put(`/merchants/${merchantId}`, putBody);
      await api.post("/rate-versions", {
        scope: "merchant_mid",
        merchant_id: merchantId,
        effective_from: `${effective_from}T00:00:00Z`,
        rate_type: "Basis Points",
        rate_value: Number(newRate),
        reason: reason || `MDR change · effective ${effective_from}`,
      });
      if (isFuture) {
        toast.success(`Future-dated rate version saved · activates on ${effective_from}`, {
          description: "Current display rate stays unchanged until then. Calculations after the effective date will use the new rate automatically.",
          duration: 7000,
        });
      } else {
        toast.success(`Saved · new rate version effective ${effective_from}`);
      }
      setRateChangePending(null);
      setOpen(false); setShowRateCardForm(false); setModalTab("form");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (id) => {
    // Guard against orphaned rows with empty/whitespace merchant_id — the
    // DELETE URL would resolve to /api/merchants/ which doesn't match the
    // route and surfaces as a Network Error in the browser. Offer the purge
    // path instead.
    if (!id || !String(id).trim()) {
      if (!await openConfirm({
        title: "Delete orphaned merchant rows?",
        message: "This row has no merchant ID and can't be deleted normally. Run a one-time cleanup that removes ALL merchant rows with empty IDs?",
        confirmLabel: "Run cleanup",
        danger: true,
      })) return;
      try {
        const { data } = await api.post("/merchants/purge-orphaned");
        toast.success(`Removed ${data.removed} orphaned merchant${data.removed === 1 ? "" : "s"}`);
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
      return;
    }
    if (!await openConfirm({ title: "Delete merchant?", message: "The merchant and any auto-created program will be removed. This is not reversible.", confirmLabel: "Delete merchant", danger: true })) return;
    try {
      await api.delete(`/merchants/${id}`); toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const onBulkImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/merchants/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportReport({ ...data, filename: file.name });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // CSV of the rows the server refused (existing-in-DB or same-CSV duplicates)
  // so the admin can eyeball or fix and re-upload just the conflicting rows.
  const downloadDuplicates = () => {
    if (!importReport?.duplicates?.length) return;
    const header = "row,merchant_id,merchant_name_in_csv,existing_name,existing_gateway,existing_lob,reason\n";
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = importReport.duplicates.map((d) =>
      [d.row, d.merchant_id, d.merchant_name_csv, d.existing_name, d.existing_gateway, d.existing_lob, d.source].map(esc).join(",")
    ).join("\n");
    const blob = new Blob([header + body + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `merchants_import_duplicates_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const csv = "merchant_id,merchant_name,parent_group,notes,gateway,lob,partner_id,merchant_rate,status,settlement_type\nDEMO001,Demo Merchant,,Sample row,Cashfree,Fastag,P001,0.0105,Active,T+1\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "merchants_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // PR-D — Mark a previously-live MID as no longer transacting. Historical
  // calculations stay untouched (they read from closed-period snapshots);
  // only forward-looking views filter on `status`. Re-activation is one
  // click and preserves the deactivation timeline in `status_history`.
  const openDeactivate = (row) => {
    const today = new Date().toISOString().slice(0, 10);
    setDeactivateForm({ deactivation_date: today, reason: "" });
    setDeactivateFor(row);
  };
  const submitDeactivate = async () => {
    if (!deactivateFor) return;
    if (!deactivateForm.deactivation_date) { toast.error("Pick a deactivation date"); return; }
    if (!(deactivateForm.reason || "").trim()) { toast.error("Reason is required"); return; }
    setDeactivating(true);
    try {
      // Send full ISO datetime so the backend stores tz-aware UTC.
      const isoDate = `${deactivateForm.deactivation_date}T00:00:00Z`;
      await api.post(`/merchants/${deactivateFor.merchant_id}/deactivate`, {
        deactivation_date: isoDate,
        reason: deactivateForm.reason.trim(),
      });
      toast.success(`${deactivateFor.merchant_id} deactivated`);
      setDeactivateFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate");
    }
    setDeactivating(false);
  };
  const reactivateMerchant = async (row) => {
    if (!window.confirm(`Re-activate ${row.merchant_id} (${row.merchant_name})?\n\nThis appends to the lifecycle log; the prior deactivation entry stays in history.`)) return;
    try {
      await api.post(`/merchants/${row.merchant_id}/reactivate`, { reason: "Re-activated via merchant list" });
      toast.success(`${row.merchant_id} re-activated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to re-activate");
    }
  };

  const getSortValue = (r, key) => {
    const p = programByMid[r.merchant_id];
    switch (key) {
      case "merchant_id": return r.merchant_id || "";
      case "merchant_name": return (r.merchant_name || "").toLowerCase();
      case "gateway": return (r.gateway || p?.gateway || "").toLowerCase();
      case "lob": return (r.lob || p?.lob || "").toLowerCase();
      case "base_rate": return Number(p?.base_rate ?? -1);
      case "merchant_rate": return Number(p?.merchant_rate ?? r.merchant_rate ?? -1);
      case "partner": {
        const pid = p?.referred_partner_id || r.partner_id;
        return (pid && pid !== "P000" ? partnerName(pid) : "").toLowerCase();
      }
      case "share": return Number(p?.partner_share_value ?? -1);
      case "settlement_type": return (r.settlement_type || "").toLowerCase();
      case "status": return (r.status || "Active").toLowerCase();
      default: return "";
    }
  };

  const statusOptions = useMemo(() => {
    // Drop the legacy "No program" sentinel — it's no longer relevant since
    // merchants and programs are 1-to-1 via auto-program creation. Keep the
    // canonical values + anything stored on actual rows so admins don't lose
    // a custom value (e.g. "In Risk Review").
    const set = new Set((meta.merchant_statuses || []));
    rows.forEach((r) => { if (r.status) set.add(r.status); });
    set.delete("No program");
    return [...set].sort();
  }, [meta.merchant_statuses, rows]);

  // Pre-compute which merchant names already have a portal account so the
  // table can disambiguate (Paysh has multiple MIDs but only one portal user).
  const [grantedNames, setGrantedNames] = useState(new Set());
  const [grantBump, setGrantBump] = useState(0);
  useEffect(() => {
    let mounted = true;
    api.get("/admin/access/list").then(({ data }) => {
      if (!mounted) return;
      const names = new Set();
      (data || []).filter((u) => u.role === "merchant" && u.linked_merchant_name).forEach((u) => {
        names.add(String(u.linked_merchant_name).trim().toLowerCase());
      });
      setGrantedNames(names);
    }).catch(() => { /* admin-only — silent for non-admins */ });
    return () => { mounted = false; };
  }, [rows, grantBump]);
  const merchantHasAccess = (m) => grantedNames.has(String(m.merchant_name || "").trim().toLowerCase());

  const filtered = useMemo(() => {
    let list = rows.filter((r) => {
      if (q && !r.merchant_id.toLowerCase().includes(q.toLowerCase()) && !r.merchant_name.toLowerCase().includes(q.toLowerCase())) return false;
      const p = programByMid[r.merchant_id];
      const gw = r.gateway || p?.gateway || "";
      const lob = r.lob || p?.lob || "";
      const pid = p?.referred_partner_id || r.partner_id || "";
      const secPid = p?.secondary_partner_id || r.secondary_partner_id || "";
      if (filterGateway && gw !== filterGateway) return false;
      if (filterLob && lob !== filterLob) return false;
      if (filterPartner && pid !== filterPartner && secPid !== filterPartner) return false;
      if (filterStatus) {
        const statusKey = r.status || "Active";
        if (statusKey !== filterStatus) return false;
      }
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = getSortValue(a, key), vb = getSortValue(b, key);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb)) * mul;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, filterGateway, filterLob, filterPartner, filterStatus, sortBy, programByMid, partners]);

  const toggleSort = (key) => {
    setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const clearFilters = () => { setFilterGateway(""); setFilterLob(""); setFilterPartner(""); setFilterStatus(""); setQ(""); };
  const hasActiveFilters = q || filterGateway || filterLob || filterPartner || filterStatus;

  const toggleSelected = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((s) => {
      const visible = filtered.map((r) => r.merchant_id);
      const allSelected = visible.length > 0 && visible.every((id) => s.has(id));
      if (allSelected) {
        const next = new Set(s);
        visible.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(s);
      visible.forEach((id) => next.add(id));
      return next;
    });
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.merchant_id));
  const applyBulkSettlement = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error("Select at least one merchant"); return; }
    try {
      const { data } = await api.post("/merchants/bulk-update-settlement", {
        merchant_ids: ids,
        settlement_type: bulkSettlement.value,
      });
      toast.success(`Updated ${data.modified} merchant${data.modified === 1 ? "" : "s"} → ${bulkSettlement.value || "cleared"}`);
      setBulkSettlement({ open: false, value: "" });
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  return (
    <div data-testid="merchants-page">
      <PageHeader
        title="Merchants"
        actions={<>
          <input ref={fileRef} type="file" accept=".csv" onChange={onBulkImport} className="hidden" data-testid="merchant-csv-input" />
          <Btn variant="ghost" testid="csv-template-btn" onClick={downloadTemplate}>Template</Btn>
          <Btn variant="ghost" testid="bulk-import-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Upload size={14} /> {importing ? "Importing…" : "Import CSV"}
          </Btn>
          {selectedIds.size > 0 && (
            <Btn variant="ghost" testid="bulk-edit-settlement-btn" onClick={() => setBulkSettlement({ open: true, value: "T+1" })}>
              Set Settlement · {selectedIds.size} selected
            </Btn>
          )}
          <div className="w-56"><Input placeholder="Search merchants…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="merchant-search" /></div>
          <Btn variant="accent" testid="add-merchant-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShowRateCardForm(false); setModalTab("form"); setOpen(true); }}><Plus size={14} /> New Merchant</Btn>
        </>}
      />
      <div className="p-4 sm:p-10">
        <MerchantsTable
          filterGateway={filterGateway} setFilterGateway={setFilterGateway}
          filterLob={filterLob} setFilterLob={setFilterLob}
          filterPartner={filterPartner} setFilterPartner={setFilterPartner}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}
          gateways={gateways} lobs={meta.lobs} partners={partners}
          statusOptions={statusOptions} partnerName={partnerName}
          filtered={filtered} programByMid={programByMid}
          taggedByMerchant={taggedByMerchant}
          volumeDeltas={volumeDeltas}
          sortBy={sortBy} onToggleSort={toggleSort}
          selectedIds={selectedIds}
          allVisibleSelected={allVisibleSelected}
          onToggleSelectAll={toggleSelectAll}
          onToggleSelected={toggleSelected}
          merchantHasAccess={merchantHasAccess}
          onGrant={setGrantFor}
          onReactivate={reactivateMerchant}
          onDeactivate={openDeactivate}
          onEdit={(r) => {
            const p = programByMid[r.merchant_id];
            const storedRate = r.merchant_rate ?? p?.merchant_rate ?? null;
            const secPid = r.secondary_partner_id || p?.secondary_partner_id || "";
            const secVal = r.secondary_share_value ?? p?.secondary_share_value ?? 0;
            // iter39.3 — hydrate co_partner_shares. Prefer the merchant's
            // array (post-migration); fall back to program's array; final
            // fallback = legacy scalars promoted into a single-entry array
            // for continuity with any un-migrated demo rows.
            const rawCoShares = (r.co_partner_shares && r.co_partner_shares.length)
              ? r.co_partner_shares
              : (p?.co_partner_shares && p.co_partner_shares.length ? p.co_partner_shares : []);
            let coShares = rawCoShares.map((c) => ({
              partner_id: c.partner_id || "",
              share_type: c.share_type || "Percentage",
              // Store as % string for the UI input; matches secondary_share_value convention.
              share_value: Number(c.share_value) ? (Number(c.share_value) * 100).toFixed(4).replace(/\.?0+$/, "") : "",
            }));
            if (!coShares.length && secPid && secPid !== "P000" && Number(secVal) > 0) {
              coShares = [{
                partner_id: secPid,
                share_type: r.secondary_share_type || p?.secondary_share_type || "Percentage",
                share_value: (Number(secVal) * 100).toFixed(4).replace(/\.?0+$/, ""),
              }];
            }
            setForm({
              ...EMPTY,
              ...r,
              gateway: r.gateway || p?.gateway || "",
              lob: r.lob || p?.lob || "",
              partner_id: r.partner_id || p?.referred_partner_id || "P000",
              merchant_rate: storedRate != null ? (Number(storedRate) * 100).toFixed(4).replace(/\.?0+$/, "") : "",
              secondary_partner_id: secPid,
              secondary_share_type: r.secondary_share_type || p?.secondary_share_type || "Percentage",
              secondary_share_value: Number(secVal) ? (Number(secVal) * 100).toFixed(4).replace(/\.?0+$/, "") : "",
              co_partner_shares: coShares,
            });
            setEditId(r.merchant_id); setShowRateCardForm(false); setModalTab("form"); setOpen(true);
          }}
          onRemove={remove}
        />
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setShowRateCardForm(false); setModalTab("form"); }} title={editId ? "Edit Merchant" : "New Merchant"} size="lg">
        {editId && (
          <ModalTabs
            testid="merchant-modal-tabs"
            active={modalTab}
            onChange={setModalTab}
            tabs={[
              { id: "form", label: "Details", testid: "merchant-tab-form" },
              { id: "rate-history", label: "Rate History", testid: "merchant-tab-rate-history" },
            ]}
          />
        )}
        {modalTab === "rate-history" && editId ? (
          <RateHistoryTab
            scope="merchant_mid"
            keyFields={{ merchant_id: editId }}
            currentRate={form.merchant_rate ? pctToDec(form.merchant_rate) : null}
            onCreated={() => { /* refresh merchants list once parent reloads */ }}
          />
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant ID <span className="text-[var(--negative)]">*</span></label>
            <Input data-testid="form-merchant-id" value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} disabled={!!editId} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant Name <span className="text-[var(--negative)]">*</span></label>
            <MerchantNameAutocomplete
              testid="form-merchant-name"
              value={form.merchant_name}
              onChange={(v) => setForm({ ...form, merchant_name: v })}
              existingMerchants={rows}
              excludeMerchantId={editId}
            /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Parent Group</label>
            <Input value={form.parent_group} onChange={(e) => setForm({ ...form, parent_group: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Status <span className="text-[var(--negative)]">*</span></label>
            <ComboSelect
              testid="form-merchant-status"
              options={statusOptions}
              value={form.status || ""}
              onChange={(v) => setForm({ ...form, status: v })}
              placeholder="Select status"
            />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Pick from the list, or use "+ Add new…" to create a new value (e.g. "In Risk Review").</div></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
            <div className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider mb-2">Commercial Setup</div>
          </div>

          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Gateway <span className="text-[var(--negative)]">*</span></label>
            <Select testid="form-m-gateway" placeholder="Select gateway" options={gateways.map((g) => g.gateway_name)} value={form.gateway || ""} onChange={(v) => setForm({ ...form, gateway: v, lob: "" })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">LOB <span className="text-[var(--negative)]">*</span></label>
            <Select
              testid="form-m-lob"
              options={gatewayLobOptions}
              value={form.lob || ""}
              onChange={(v) => setForm({ ...form, lob: v })}
              placeholder={form.gateway ? "Select LOB" : "Pick a gateway first"}
              disabled={!form.gateway}
            />
            {form.gateway && gatewayLobOptions.length === 0 && (
              <div className="text-[10.5px] text-amber-700 mt-1" data-testid="form-m-no-lobs">
                <b>{form.gateway}</b> has no LOBs configured.{" "}
                <Link to="/gateways" className="underline hover:text-amber-900">
                  Go to Payment Gateways → add one
                </Link>
                , then come back.
              </div>
            )}
          </div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Partner</label>
            <Select testid="form-m-partner" options={partners.map((p) => ({ value: p.partner_id, label: `${p.partner_id} · ${p.partner_name}` }))} value={form.partner_id} onChange={(v) => { setForm({ ...form, partner_id: v }); setShowRateCardForm(false); }} />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Leave as <code>P000 · No partner</code> for direct merchants where no commission is paid out.</div></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Settlement Type</label>
            <Select
              testid="form-m-settlement-type"
              options={SETTLEMENT_OPTIONS}
              value={form.settlement_type || ""}
              onChange={(v) => setForm({ ...form, settlement_type: v })}
              placeholder="— select —"
            />
            <div className="text-[10.5px] text-[var(--muted)] mt-1"><b>IS</b> = Instant Settlement · <b>T+1</b> = next-business-day.</div></div>

          {showConflict && !showRateCardForm && (
            <div className="col-span-2 flex items-center gap-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-sm" data-testid="rate-card-conflict">
              <AlertTriangle size={16} className="text-amber-700 shrink-0" />
              <div className="flex-1 text-[12.5px] text-amber-900">
                <span className="font-semibold">{partnerName(form.partner_id)}</span> has no rate card for <span className="font-semibold">{form.gateway} · {form.lob}</span>. Add one now to compute commission.
              </div>
              <Btn variant="accent" testid="open-add-rate-card" onClick={() => setShowRateCardForm(true)}><Plus size={12} /> Add rate card</Btn>
            </div>
          )}

          {showConflict && showRateCardForm && (
            <div className="col-span-2 px-3 py-3 bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm" data-testid="add-rate-card-panel">
              <div className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider mb-2">
                New rate card for {partnerName(form.partner_id)} — {form.gateway} · {form.lob}
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.0001" placeholder="0.0095 (= 0.95%)" value={newRateValue} onChange={(e) => setNewRateValue(e.target.value)} data-testid="new-rate-value" />
                <Btn variant="ghost" onClick={() => { setShowRateCardForm(false); setNewRateValue(""); }}>Cancel</Btn>
                <Btn variant="accent" testid="save-new-rate-card" onClick={addRateCardOnFly}>Save card</Btn>
              </div>
              <div className="text-[11px] text-[var(--muted)] mt-1.5">Saves to the partner's rate cards immediately and re-pulls the value below.</div>
            </div>
          )}

          <div className="col-span-2 grid grid-cols-3 gap-2 bg-[var(--surface-2)] border border-[var(--line)] p-3 rounded-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">PG Base Rate</div>
              <div className="num text-sm font-semibold mt-0.5">{pgBaseRate !== null && pgBaseRate !== undefined ? fmtBps(pgBaseRate) : <span className="text-[var(--muted)] font-normal">— pick PG/LOB</span>}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Partner Base Rate</div>
              <div className="num text-sm font-semibold mt-0.5">{partnerBaseRate !== null && partnerBaseRate !== undefined ? fmtBps(partnerBaseRate) : <span className="text-[var(--muted)] font-normal">— no rate card</span>}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Partner Commission</div>
              <div className={`num text-sm font-semibold mt-0.5 ${partnerCommissionBps > 0 && partnerBaseRate !== null ? "text-[var(--accent)]" : "text-[var(--muted)] font-normal"}`}>
                {form.merchant_rate && partnerBaseRate !== null ? `${(partnerCommissionBps / 100).toFixed(2)}% of volume` : "—"}
              </div>
            </div>
          </div>

          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Merchant Rate (%) <span className="text-[var(--negative)]">*</span></label>
            <div className="relative">
              <Input type="number" step="0.01" data-testid="form-m-rate" value={form.merchant_rate} onChange={(e) => setForm({ ...form, merchant_rate: e.target.value })} placeholder="1.02" className="pr-7" />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[var(--muted)] pointer-events-none">%</span>
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1">Enter as % (e.g. 1.02 for 1.02%). If filled along with Gateway + LOB, a backing program is auto-created for this merchant.</div></div>

          <div className="col-span-2 border-t border-[var(--line)] pt-3 mt-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Co-partner shares (optional override — paid on top)</div>
                <div className="text-[11px] text-[var(--muted)] mt-0.5">Each entry gets their share on top of the primary partner&apos;s commission. Primary partner&apos;s share is unchanged. Add as many as you need.</div>
              </div>
              <Btn
                variant="ghost"
                testid="add-co-partner"
                onClick={() => setForm({ ...form, co_partner_shares: [...(form.co_partner_shares || []), { partner_id: "", share_type: "Percentage", share_value: "" }] })}
              >
                <Plus size={12} /> Add co-partner
              </Btn>
            </div>
            <div className="space-y-2">
              {(form.co_partner_shares || []).map((c, i) => {
                const pickedElsewhere = new Set(
                  (form.co_partner_shares || [])
                    .map((x, j) => (j !== i ? x.partner_id : ""))
                    .filter(Boolean)
                );
                const partnerOpts = [
                  { value: "", label: "— pick partner —" },
                  ...partners
                    .filter((p) => p.partner_id !== "P000" && p.partner_id !== form.partner_id && !pickedElsewhere.has(p.partner_id))
                    .map((p) => ({ value: p.partner_id, label: `${p.partner_id} · ${p.partner_name}` })),
                ];
                const updateCo = (patch) => {
                  const next = [...(form.co_partner_shares || [])];
                  next[i] = { ...next[i], ...patch };
                  setForm({ ...form, co_partner_shares: next });
                };
                const removeCo = () => {
                  const next = (form.co_partner_shares || []).filter((_, j) => j !== i);
                  setForm({ ...form, co_partner_shares: next });
                };
                return (
                  <div key={`co-${i}`} data-testid={`co-partner-row-${i}`} className="grid grid-cols-[1fr,120px,120px,32px] gap-2 items-start bg-[var(--surface-2)] border border-[var(--line)] p-2.5 rounded-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Co-partner #{i + 1}</div>
                      <Select
                        testid={`co-partner-${i}`}
                        placeholder="Pick partner"
                        options={partnerOpts}
                        value={c.partner_id || ""}
                        onChange={(v) => updateCo({ partner_id: v })}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Share type</div>
                      <Select
                        testid={`co-share-type-${i}`}
                        options={[{ value: "Percentage", label: "% of Gross" }, { value: "Basis Points", label: "bps of Volume" }]}
                        value={c.share_type || "Percentage"}
                        onChange={(v) => updateCo({ share_type: v })}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-1">Share (%)</div>
                      <div className="relative">
                        <Input
                          type="number" step="0.01"
                          data-testid={`co-share-value-${i}`}
                          value={c.share_value}
                          onChange={(e) => updateCo({ share_value: e.target.value })}
                          disabled={!c.partner_id}
                          placeholder="4"
                          className="pr-7 text-right num"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[var(--muted)] pointer-events-none">%</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeCo}
                      data-testid={`co-partner-remove-${i}`}
                      title="Remove this co-partner"
                      className="mt-[22px] text-[var(--muted)] hover:text-[var(--negative)] p-1.5"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              {!(form.co_partner_shares || []).length && (
                <div className="text-[12px] text-[var(--muted)] text-center py-3 border border-dashed border-[var(--line-2)] rounded-sm">
                  No co-partners yet. Click <b>Add co-partner</b> to route part of this merchant&apos;s commission to another partner.
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => { setOpen(false); setShowRateCardForm(false); setModalTab("form"); }}>Cancel</Btn>
          {modalTab === "form" && <Btn variant="accent" testid="save-merchant" onClick={save}>Save</Btn>}
        </div>
      </Modal>

      <RateChangeDialog
        open={!!rateChangePending}
        onClose={() => setRateChangePending(null)}
        onConfirm={confirmRateChange}
        oldRate={rateChangePending?.oldRate}
        newRate={rateChangePending?.newRate}
        entityLabel={rateChangePending ? `Merchant ${rateChangePending.merchantId}${rateChangePending.merchantName ? ` · ${rateChangePending.merchantName}` : ""}` : ""}
      />

      <GrantAccessModal
        open={!!grantFor}
        onClose={() => { setGrantFor(null); setGrantBump((n) => n + 1); }}
        role="merchant"
        subject={grantFor || {}}
      />

      {/* Bulk-edit Settlement Type — operator selects rows above, then this
          modal applies a single value across the entire selection. */}
      <MerchantBulkSettlementModal
        open={bulkSettlement.open}
        onClose={() => setBulkSettlement({ open: false, value: "" })}
        value={bulkSettlement.value}
        setValue={(v) => setBulkSettlement({ ...bulkSettlement, value: v })}
        count={selectedIds.size}
        onApply={applyBulkSettlement}
      />

      {/* Import summary — shown after every bulk-import. Splits inserted vs
          duplicates vs errors so the admin knows exactly what made it in, and
          ships a "Download duplicates" CSV for reconciliation. */}
      <MerchantImportReportModal
        report={importReport}
        onClose={() => setImportReport(null)}
        onDownloadDuplicates={downloadDuplicates}
      />

      {/* Deactivate-MID modal — captures the date the MID stopped
          transacting + a reason. Past calculations stay frozen because they
          read from `closed_periods.totals`; only forward-looking views
          filter on `status`. */}
      <MerchantDeactivateModal
        open={!!deactivateFor}
        target={deactivateFor}
        form={deactivateForm}
        setForm={setDeactivateForm}
        busy={deactivating}
        onCancel={() => setDeactivateFor(null)}
        onConfirm={submitDeactivate}
      />
    </div>
  );
}
